export type AscEventParams = Record<string, string | number | boolean>

type Gtag = ((...args: unknown[]) => void) & { daRoutesVendorEvents?: boolean }

export interface AscHostWindow {
  dataLayer?: unknown[]
  asc_dataLayer?: unknown
  asc_datalayer?: unknown
  gtag?: Gtag
  google_tag_manager?: Record<string, unknown>
}

export interface AscHostDeliveryOptions {
  configWaitMs?: number
  pollMs?: number
}

const MEASUREMENT_ID = /^G-[A-Z0-9]+$/i

/**
 * Sends our ASC events from the dealer page, so GA4 counts them under the shopper's own client and session.
 *
 * Dealer Alchemist's EventsManager wraps `window.gtag` and routes any vendor `gtag('event', 'asc_*')` without
 * `send_to` to the store's tracking IDs; there, the page decides the destinations. Everywhere else we send to the
 * IDs we resolve and mirror the event into `window.dataLayer` as `dl_<name>` for the dealer's own GTM. A page
 * running the universal ASC listener (GTM-5FC28SQ) is no exception: it only reacts to postMessage payloads
 * carrying its own key, which we never send, so it cannot deliver the same event a second time.
 */
export function createAscHostDelivery(win: AscHostWindow, options: AscHostDeliveryOptions = {}) {
  const { configWaitMs = 5000, pollMs = 100 } = options
  let sending: Promise<void> = Promise.resolve()
  let configWaitDone = false

  const gtag = (...args: unknown[]) => {
    if (typeof win.gtag === 'function') return win.gtag(...args)
    const dataLayer = (win.dataLayer = win.dataLayer || [])
    // gtag.js only processes `arguments` objects, never plain arrays.
    function push(..._args: unknown[]) {
      // eslint-disable-next-line prefer-rest-params
      dataLayer.push(arguments)
    }
    push(...args)
  }

  const deliver = async (name: string, params: AscEventParams) => {
    if (win.gtag?.daRoutesVendorEvents === true) return gtag('event', name, params)

    const ids = resolveAscMeasurementIds(win)
    if (ids.length === 0) return

    // Once per page: a property that never shows a config must not delay every later event as well.
    if (!configWaitDone) await waitForGtagConfig(win, ids, configWaitMs, pollMs)
    configWaitDone = true
    const eventModel = { ...params, send_to: ids }
    gtag('event', name, eventModel)
    win.dataLayer = win.dataLayer || []
    win.dataLayer.push({ event: `dl_${name}`, eventModel })
  }

  /** Delivers in call order, since a wait for gtag config would otherwise let a later event overtake. */
  return function sendAscEvent(name: string, params: AscEventParams): Promise<void> {
    if (!name.startsWith('asc_')) return sending
    sending = sending.then(() => deliver(name, params)).catch(() => {})
    return sending
  }
}

/**
 * The dealer's GA4 IDs: `measurement_ids` of an object-shaped `asc_datalayer`, merged with the `send_to` of the
 * site's own `asc_*` gtag events. Never every `G-` ID on the page: some pages configure placeholders and unrelated
 * properties.
 */
export function resolveAscMeasurementIds(win: AscHostWindow): string[] {
  const ids = new Set<string>()
  const add = (value: unknown) => {
    for (const id of Array.isArray(value) ? value : [value]) {
      if (typeof id === 'string' && MEASUREMENT_ID.test(id)) ids.add(id.toUpperCase())
    }
  }

  const ascDataLayer = win.asc_dataLayer || win.asc_datalayer
  if (isRecord(ascDataLayer)) add(ascDataLayer.measurement_ids)

  for (const entry of win.dataLayer ?? []) {
    const params = siteAscEventParams(entry)
    if (params) add(params.send_to)
  }

  // Carried over from GTM-NCZQZGL6's destination variable: when this property is listed, it is the only one.
  if (ids.has('G-ERETQ9FKD4')) return ['G-ERETQ9FKD4']
  return [...ids]
}

function siteAscEventParams(entry: unknown): Record<string, unknown> | null {
  if (!isArgumentsLike(entry)) return null
  const [command, name, params] = [entry[0], entry[1], entry[2]]
  if (command !== 'event' || typeof name !== 'string' || !name.startsWith('asc_')) return null
  if (!isRecord(params) || params.event_owner === 'lectrium') return null
  return params
}

function waitForGtagConfig(win: AscHostWindow, ids: string[], timeoutMs: number, pollMs: number): Promise<void> {
  const isConfigured = (id: string) =>
    Boolean(win.google_tag_manager?.[id]) ||
    (win.dataLayer ?? []).some(entry => isArgumentsLike(entry) && entry[0] === 'config' && entry[1] === id)

  return new Promise(resolve => {
    const startedAt = Date.now()
    const poll = () => {
      if (ids.every(isConfigured) || Date.now() - startedAt >= timeoutMs) return resolve()
      setTimeout(poll, pollMs)
    }
    poll()
  })
}

function isArgumentsLike(value: unknown): value is ArrayLike<unknown> {
  return typeof value === 'object' && value !== null && 'length' in value && typeof value.length === 'number'
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
